import { MoreHorizontal, Star, Trash2 } from "lucide-react"
import * as React from "react"

import { Avatar } from "@/components/domain/avatar"
import { Button } from "@/components/ui/button"
import { Combobox, type ComboboxOption } from "@/components/ui/combobox"
import { Dialog } from "@/components/ui/dialog"
import { IconButton } from "@/components/ui/icon-button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/popover"
import { formatRelative } from "@/lib/format-date"

import { GrantAccessDialog, RevokeAccessDialog } from "./clients-dialogs"
import type { ClientMember, ClientRow, ClientUserOption, UserId } from "./clients-data"

export interface MemberActions {
  onAddMember: (userId: UserId) => void
  onRemoveMember: (userId: UserId) => void
  onMakePrimary: (userId: UserId) => void
}

export function ExpandedMembers({
  row,
  users,
  actions,
}: {
  row: ClientRow
  users: readonly ClientUserOption[]
  actions: MemberActions
}): JSX.Element {
  const [pick, setPick] = React.useState<string | null>(null)
  const [pendingRemove, setPendingRemove] = React.useState<ClientMember | null>(null)
  const [pendingAdd, setPendingAdd] = React.useState<UserId | null>(null)

  const memberIds = React.useMemo(() => new Set(row.members.map((m) => m.userId)), [row.members])
  const options: ComboboxOption[] = users
    .filter((u) => !memberIds.has(u.id))
    .map((u) => ({ value: u.id, label: `${u.name} — ${u.email}` }))

  const pendingAddUser = pendingAdd ? (users.find((u) => u.id === pendingAdd) ?? null) : null

  return (
    <div className="border-l-2 border-brand/40 bg-brand-tint/15 py-4 pl-[60px] pr-6">
      {row.members.length === 0 ? (
        <p className="text-body-sm text-text-3">
          No members yet. A client login gains access to this engagement only after being added
          here.
        </p>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {row.members.map((mem) => (
            <MemberRow
              key={mem.userId}
              engagement={row}
              member={mem}
              onMakePrimary={() => actions.onMakePrimary(mem.userId)}
              onRemove={() => setPendingRemove(mem)}
            />
          ))}
        </ul>
      )}

      <div className="mt-4 flex items-center gap-2">
        <div className="min-w-0 flex-1 sm:max-w-[360px]">
          <Combobox
            options={options}
            value={pick}
            onValueChange={setPick}
            placeholder={
              options.length === 0 ? "Every client account is a member" : "Add a client contact"
            }
            searchPlaceholder="Search contacts"
            disabled={options.length === 0}
            aria-label={`Add contact to ${row.name}`}
          />
        </div>
        <Button size="sm" disabled={!pick} onClick={() => setPendingAdd(pick as UserId)}>
          Grant access
        </Button>
      </div>

      <Dialog
        open={pendingAdd !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAdd(null)
        }}
      >
        {pendingAddUser && (
          <GrantAccessDialog
            engagement={row}
            user={pendingAddUser}
            onConfirm={() => {
              actions.onAddMember(pendingAddUser.id)
              setPendingAdd(null)
              setPick(null)
            }}
          />
        )}
      </Dialog>

      <Dialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemove(null)
        }}
      >
        {pendingRemove && (
          <RevokeAccessDialog
            engagement={row}
            member={pendingRemove}
            onConfirm={() => {
              actions.onRemoveMember(pendingRemove.userId)
              setPendingRemove(null)
            }}
          />
        )}
      </Dialog>
    </div>
  )
}

function MemberRow({
  engagement,
  member,
  onMakePrimary,
  onRemove,
}: {
  engagement: ClientRow
  member: ClientMember
  onMakePrimary: () => void
  onRemove: () => void
}): JSX.Element {
  const neverSignedIn = member.lastSeen === null
  return (
    <li className="flex items-center gap-2.5 rounded-md border border-border-soft bg-surface px-3 py-2">
      <Avatar name={member.name} identity={member.userId} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-body-sm font-medium text-text">{member.name}</span>
          {member.isPrimary && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-brand-tint px-1.5 py-px text-micro font-semibold text-brand"
              title="Primary contact — reports and notifications address this person"
            >
              <Star className="size-2.5 fill-current" aria-hidden="true" />
              Primary
            </span>
          )}
          {neverSignedIn && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-print-tint px-1.5 py-px text-micro font-semibold text-print"
              title="Invited but has never signed in"
            >
              Never signed in
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-1.5 text-caption text-text-3">
          <span className="truncate font-id">{member.email}</span>
          <span aria-hidden="true">·</span>
          <span className="shrink-0">
            {neverSignedIn ? "invited" : `last seen ${formatRelative(member.lastSeen!)}`}
          </span>
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton
            size="sm"
            variant="ghost"
            label={`Actions for ${member.name}`}
            icon={<MoreHorizontal />}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!member.isPrimary && (
            <DropdownMenuItem icon={<Star />} onSelect={onMakePrimary}>
              Make primary contact
            </DropdownMenuItem>
          )}
          <DropdownMenuItem icon={<Trash2 />} destructive onSelect={onRemove}>
            Revoke access from {engagement.name}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  )
}
